import type { World } from '../sim/types';
import { REEF_ISLANDS, type ReefIsland } from '../sim/reef';
import { CLEARED_T_PER_DAY, shoreDebrisT } from '../sim/shoreDebris';
import { SHORE_DAYS } from './goals';
import { NET_SHARE } from './data/gearShare';

/**
 * The derelict gear drawn (M338, `docs/CLARITY_ROADMAP.md`'s C6, the user's word: "incorporate actual debris and nets in
 * the ocean to give players visual feedback"), on the shores (Explore's "Your work on the reef") and snagged on the reef
 * (the scene's), both from the shores' tonnes the world holds (`shoreDebrisT`, M307), so a cleanup home takes its haul
 * off both the day it lands.
 *
 * GAME CHOICE (M178; the user's word, M338: "both but no need to say that it's exaggerated"): one piece drawn for each
 * cleanup trip's haul, `NET_DRAWN_T`, on the shores and on the reef alike, its tonnes rounded to the nearest, so each
 * trip home takes one away (review M338's: rounded up, a cleared shore drew a whole net again the next day at 0.0 t). The reef's count follows the shores' tonnes, not a measured density underwater:
 * NOAA's towed divers counted 144 pieces of derelict gear over 392.49 km of Maui Nui's forereef in 2005 to 2010
 * (`scripts/data/towedDebris.ts`, NCEI 0163745, MEASURED), which over the scene's 96 m of reef would draw none on most
 * days. Nothing on screen says the reef's count is denser than measured, at the user's word; this note and
 * `docs/KNOWN_ISSUES.md`'s M338 say it.
 */

/** A piece drawn for each cleanup trip's haul, tonnes: a trip's five days at half a tonne a day, 2.5 t. */
export const NET_DRAWN_T = CLEARED_T_PER_DAY * SHORE_DAYS;

/** The most nets drawn on one island's shore (JUDGED: three rows of the strip at 390 px; Lānaʻi's 32.4 t at the opening draws 13, two rows; 50 t, about eight years uncleaned at its 2.3 t a year, reach it). */
export const NETS_DRAWN_MOST = 20;

/**
 * The most pieces drawn on the reef (JUDGED: the 40 m of reef about the camera's home crowded past it; the opening's 64.5 t
 * draws 26, and the shores' 13.9 t a year come ashore again reach 150 t in about six years with no cleanup). Past it a
 * cleanup is drawn on the reef only once the tonnes are under it again.
 */
export const REEF_GEAR_MOST = 60;

/** One piece in `NET_EVERY` drawn a net, the rest fishing line: the towed divers' share of nets, 29 of 144 (`NET_SHARE`), as one in five. */
export const NET_EVERY = Math.round(1 / NET_SHARE);

export type GearKind = 'large' | 'small' | 'line';

/** A piece drawn: a large net, a small fragment of one, or a tangle of line; a net fouled or clean (the line has one picture). */
export interface GearPiece {
  kind: GearKind;
  fouled: boolean;
}

/**
 * The k-th net drawn, the shore's and the reef's alike: one in three large, every other fouled (JUDGED: a mix of gear new
 * to the reef and gear lain there long enough for the algae to grow over it; nothing found measures the share at Maui Nui).
 */
export const netPiece = (j: number): GearPiece => ({ kind: j % 3 === 0 ? 'large' : 'small', fouled: j % 2 === 1 });

/** The nets drawn on an island's shore today: one a trip's haul of its tonnes, rounded, `NETS_DRAWN_MOST` at most. */
export function shoreNets(world: World, island: ReefIsland): GearPiece[] {
  const n = Math.min(NETS_DRAWN_MOST, Math.round(shoreDebrisT(world, island) / NET_DRAWN_T));
  return Array.from({ length: n }, (_, j) => netPiece(j));
}

/**
 * The pieces snagged on the reef today, in their fixed order (a cleanup takes the last, so each piece keeps its place):
 * one a trip's haul of all four shores' tonnes, rounded, `REEF_GEAR_MOST` at most; each `NET_EVERY`th a net, the rest line.
 */
export function reefGear(world: World): GearPiece[] {
  const tonnes = REEF_ISLANDS.reduce((s, island) => s + shoreDebrisT(world, island), 0);
  const n = Math.min(REEF_GEAR_MOST, Math.round(tonnes / NET_DRAWN_T));
  return Array.from({ length: n }, (_, k) => (k % NET_EVERY === 0 ? netPiece(k / NET_EVERY) : { kind: 'line', fouled: false }));
}
